import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

import Sidebar from '../layouts/Sidebar';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import DataTable from '../components/ui/DataTable';
import AuditService from '../services/AuditService';

const CATEGORY_BADGE = { auth: 'blue', admin: 'red', mobile: 'green', operational: 'amber' };
const PAGE_SIZE = 25;

export default function AuditLogs() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize: PAGE_SIZE };
      if (category) params.category = category;
      const { data } = await AuditService.findAll(params);
      setRows(data.rows);
      setTotal(data.total);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Audit logs are restricted to administrators.');
      } else {
        toast.error('Failed to load audit logs.');
      }
    } finally {
      setLoading(false);
    }
  }, [page, category]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const headCells = [
    {
      id: 'createdAt',
      label: 'Timestamp',
      render: (row) => (
        <span className="font-mono text-xs text-neutral-500">
          {new Date(row.createdAt).toLocaleString()}
        </span>
      ),
    },
    {
      id: 'memberEmail',
      label: 'User',
      render: (row) => <span className="text-neutral-700">{row.memberEmail || 'anonymous'}</span>,
    },
    {
      id: 'category',
      label: 'Category',
      render: (row) => <Badge variant={CATEGORY_BADGE[row.category] || 'neutral'}>{row.category}</Badge>,
    },
    {
      id: 'action',
      label: 'Action',
      render: (row) => <span className="font-mono text-xs text-neutral-700">{row.action}</span>,
    },
    {
      id: 'statusCode',
      label: 'Result',
      render: (row) => (
        <Badge variant={row.statusCode < 400 ? 'green' : 'red'}>{row.statusCode}</Badge>
      ),
    },
    {
      id: 'path',
      label: 'Endpoint',
      render: (row) => (
        <span className="font-mono text-xs text-neutral-400">{row.method} {row.path}</span>
      ),
    },
    { id: 'ip', label: 'IP', render: (row) => <span className="font-mono text-xs">{row.ip}</span> },
  ];

  return (
    <Sidebar title="Audit Logs">
      <section className="space-y-6">
        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
          <div>
            <h3 className="text-lg font-bold text-neutral-800">Security &amp; Operational Audit Trail</h3>
            <p className="text-xs text-neutral-500 mt-1">
              Authentication, administrative and mobile events. {total} record(s) retained.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              className="form-input w-44"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All categories</option>
              <option value="auth">Authentication</option>
              <option value="admin">Administrative</option>
              <option value="mobile">Mobile</option>
            </select>
            <Button variant="secondary" icon={RefreshCw} onClick={load}>Refresh</Button>
          </div>
        </div>

        <DataTable
          headCells={headCells}
          rows={rows}
          pageSize={PAGE_SIZE}
          emptyLabel={loading ? 'Loading audit trail…' : 'No audit records found.'}
        />

        <div className="flex items-center justify-between">
          <p className="text-xs text-neutral-400">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              icon={ChevronLeft}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              icon={ChevronRight}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      </section>
    </Sidebar>
  );
}
